// 客户端组件，负责UI渲染和交互
'use client';

import { fetchCustomerPointTask, fetchPointsMallInfo } from '@/api/account';
import { ICustomerPointTaskItem, IPointsMallInfo, IRedeemableItem } from '@/api/types/account';
import { CrmPointRule } from '@/api/types/public';
import RedeemedDetailDialog from '@/app/[variants]/account/redeemable-items/components/RedeemedDetailDialog';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useEffect, useState } from 'react';
import CouponRedeemDialog from './components/CouponRedeemDialog';
import InviteGetPoints from './components/InviteGetPoints';
import RedeemableItemsList from './components/RedeemableItemsList';
import TipsDialog from './components/TipsDialog';
import { PageContent } from './components/ui-components';
import { usePointsMallTracking } from './hooks/usePointsMallTracking';

interface PageProps {
  initData: {
    freeData: IPointsMallInfo;
    redeemableList: IRedeemableItem[];
    total: number;
  };
  pageLimit: number;
  crmPointsRules: CrmPointRule;
  customerPointTask: ICustomerPointTaskItem[];
}
export default function ClientComponent({
  deviceType = EPlatform.WEB,
  initData,
  pageLimit,
  crmPointsRules,
  customerPointTask = [],
}: PageProps & { deviceType: EPlatform }) {
  // 从initData中解构所需的数据
  const { freeData, redeemableList, total } = initData;

  // 状态定义
  const [showTips, setShowTips] = useState<boolean>(false);
  const [redeemedItem, setRedeemedItem] = useState<{ show: boolean; item: any }>({
    show: false,
    item: null,
  });

  // 从store获取客户端状态
  const storeToken = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const storeUserInfo = useStore((state) => state.userInfo);

  const [currentFreeData, setCurrentFreeData] = useState(freeData);
  const [currentCustomerPointTask, setCurrentCustomerPointTask] =
    useState<ICustomerPointTaskItem[]>(customerPointTask);

  const [showInviteGetPoints, setShowInviteGetPoints] = useState<boolean>(false);
  const [invitePayPoint, setInvitePayPoint] = useState<number>(0);

  // 埋点相关
  const { trackPointsMallView, trackPointsMallLeave } = usePointsMallTracking();

  // 监听store中的状态变化
  useEffect(() => {
    refreshFreeData();
    if (Object.keys(storeUserInfo).length > 0) {
      const _invitePayPoint = storeUserInfo?.invite_points?.invitee_pay?.point;
      setInvitePayPoint(_invitePayPoint);
      if (_invitePayPoint > 0) {
        setShowInviteGetPoints(true);
      }
    }
  }, [storeUserInfo]);

  useEffect(() => {
    // 设置为 manual 来禁用滚动恢复
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    // 组件卸载时恢复默认行为
    return () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
  }, []); // 空依赖数组意味着只在组件挂载时执行一次

  // 埋点：页面浏览和离开事件
  useEffect(() => {
    // Web端：组件挂载时触发浏览事件，卸载时触发离开事件
    trackPointsMallView();
    return () => {
      trackPointsMallLeave();
    };
  }, []);

  // 事件处理函数
  const onRedeemableItemClick = (item: any) => {
    setRedeemedItem({ show: true, item });
  };
  const refreshFreeData = async () => {
    if (storeToken && storeUserInfo?.is_member === 1) {
      const res = await fetchPointsMallInfo();
      setCurrentFreeData(res.data);
      const taskRes = await fetchCustomerPointTask();
      setCurrentCustomerPointTask(taskRes.data);
    }
  };

  const closeTips = () => setShowTips(false);
  const openTips = () => setShowTips(true);
  const closeRedeemDetail = () => setRedeemedItem({ show: false, item: null });
  return (
    <>
      {/* 页面主体内容 */}
      <PageContent
        isLoggedIn={clientTokenReady && !!storeToken}
        currentUserInfo={storeUserInfo}
        currentFreeData={currentFreeData}
        onTipsClick={openTips}
        deviceType={deviceType}
        crmPointsRules={crmPointsRules}
        customerPointTask={currentCustomerPointTask}
        refreshFreeData={refreshFreeData}
      >
        {/* 兑换商品列表组件 */}
        <RedeemableItemsList
          onRedeemableItemClick={onRedeemableItemClick}
          initialItems={redeemableList}
          initialTotal={total}
          pageLimit={pageLimit}
        />
      </PageContent>

      {/* 弹窗组件 */}
      <TipsDialog show={showTips} onClose={closeTips} />
      {/* 兑换商品详情弹窗 */}
      {redeemedItem.item?.point_morph_category === '1' && (
        <RedeemedDetailDialog
          show={redeemedItem.show}
          onClose={closeRedeemDetail}
          item={redeemedItem.item}
          isPointsMall
          pointsMallData={currentFreeData}
          refreshFreeData={refreshFreeData}
        />
      )}

      {/* 优惠券兑换弹窗 */}
      {redeemedItem.item?.point_morph_category === '2' && (
        <CouponRedeemDialog
          show={redeemedItem.show}
          onClose={closeRedeemDetail}
          item={redeemedItem.item}
          pointsMallData={currentFreeData}
          refreshFreeData={refreshFreeData}
        />
      )}

      <InviteGetPoints
        show={showInviteGetPoints}
        onClose={() => setShowInviteGetPoints(false)}
        crmPointRules={crmPointsRules}
        invitePayPoint={invitePayPoint}
      />
    </>
  );
}
